import { PlanningEntityIdentifier, PlanningEntityType } from 'generated';
import { EditAppointmentType } from 'RestAPIs/activities/types';

import { Schedule } from '../../types';

import { Box, SxProps, styled, Tooltip } from '@mui/material';

import { ScheduleTooltipBody } from '../../components';
import ActivitySummary from '../../CardsView/ActivitySummary/ActivitySummary';
import StickyHeaderPragmatic from '../StickyHeaderPragmatic/StickyHeaderPragmatic';
import InnerListPragmatic from '../InnerListPragmatic/InnerListPragmatic';
import {
  panelThemeColors,
  PanelTheme
} from '../../components/ActivityPanel/constants';
import useCopyMode from '../../hooks/useCopyMode';
import { JSX, memo, useState } from 'react';

const Panel = styled(Box)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: initial;
  contain: layout;
  isolation: isolate;
`;

interface ActivityPanelPragmaticProps extends Schedule {
  id?: string;
  activeEntityContainingSelection: string | null;
  droppableId?: string;
  headerName?: string | JSX.Element;
  planningEntityType: PlanningEntityType;
  planningEntityId: string;
  planningEntityIdentifier?: PlanningEntityIdentifier;
  readOnly?: boolean;
  onConnectClick?: () => void;
  onConnectScheduleClick?: (editAppointmentType: EditAppointmentType) => void;
  onChangeSelection?: (selectionIds: string[]) => void;
  onChangeActiveEntity?: (entityId: string) => void;
  showEmployeeName?: boolean;
  showStickyTitle?: boolean;
  showDuty?: boolean;
  showDurationDistanceTransport?: boolean;
  isOverrulingExternalDuty?: boolean;
  sx?: SxProps;
}

function ActivityPanelPragmatic(props: ActivityPanelPragmaticProps) {
  const {
    id,
    activeEntityContainingSelection,
    dayPart = 'MORNING',
    planningEntityType,
    planningEntityId,
    planningEntityIdentifier,
    appointmentGrouping,
    name,
    headerName,
    date,
    start,
    end,
    metrics: { productivity, travelKilometers },
    transportationMode,
    durationInSeconds,
    hasDuty,
    onConnectClick,
    onConnectScheduleClick,
    onChangeSelection,
    onChangeActiveEntity,
    readOnly,
    events,
    employee,
    employeeType,
    showEmployeeName,
    showStickyTitle = true,
    showDuty = true,
    showDurationDistanceTransport = true,
    startsTooEarly = false,
    endsTooLate = false,
    isLoading,
    isAssignableToEmployee = false,
    isOverrulingExternalDuty = false,
    sx
  } = props;

  const { isCopyMode } = useCopyMode();
  const [hovered, setHovered] = useState<boolean>(false);
  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);

  const { borderColor, backgroundColor } =
    panelThemeColors[dayPart as PanelTheme];

  const allowToolTip: boolean =
    hovered && !dialogIsOpen && !isCopyMode && !contextMenuOpen;

  return (
    <Panel
      sx={{
        backgroundColor,
        borderColor,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        ...(sx || {})
      }}
      className={`__${appointmentGrouping?.entityId}__`}
    >
      {showStickyTitle && (
        <StickyHeaderPragmatic
          id={appointmentGrouping?.entityId ?? planningEntityId}
          appointmentGroupingEntityId={appointmentGrouping?.entityId}
          droppable={{
            droppableId: id,
            planningEntityType,
            planningEntityId,
            planningEntityIdentifier,
            scheduleStart: start
          }}
          name={name}
          headerName={headerName}
          date={date}
          start={start}
          end={end}
          readOnly={readOnly}
          employeeId={employee?.id}
          onConnectClick={onConnectClick}
          onConnectScheduleClick={onConnectScheduleClick}
          onDialogOpen={(isOpen: boolean) => setDialogIsOpen(isOpen)}
          transportationMode={transportationMode}
          events={events}
          startsTooEarly={startsTooEarly}
          endsTooLate={endsTooLate}
          isLoading={isLoading}
          onContextMenu={(open: boolean) => setContextMenuOpen(open)}
          isAssignableToEmployee={isAssignableToEmployee}
        />
      )}
      <Tooltip
        arrow
        enterNextDelay={500}
        disableHoverListener={!allowToolTip}
        title={
          allowToolTip && (
            <ScheduleTooltipBody
              employeeTypeName={employeeType?.name}
              employeeName={employee?.nameInformal}
              appointmentGrouping={appointmentGrouping}
              planningFeatures={employee?.planningFeatures}
              onMouseOver={() => setHovered?.(false)}
            />
          )
        }
      >
        <Box
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <ActivitySummary
            planningEntityType={planningEntityType}
            productivity={productivity}
            transportationMode={transportationMode}
            travelKilometers={travelKilometers}
            employee={employee}
            hasDuty={hasDuty}
            durationInSeconds={durationInSeconds}
            employeeType={employeeType}
            showEmployeeName={showEmployeeName}
            showDuty={showDuty}
            showDurationDistanceTransport={showDurationDistanceTransport}
            isOverrulingExternalDuty={isOverrulingExternalDuty}
          />
        </Box>
      </Tooltip>
      <InnerListPragmatic
        activeEntityContainingSelection={activeEntityContainingSelection}
        dayPart={dayPart}
        onChangeSelection={onChangeSelection}
        onChangeActiveEntity={onChangeActiveEntity}
        planningEntityType={planningEntityType}
        planningEntityId={planningEntityId}
        planningEntityIdentifier={planningEntityIdentifier}
        transportationMode={transportationMode}
        scheduleStart={start}
        scheduleId={id}
        events={events}
        readOnly={readOnly}
      />
    </Panel>
  );
}

export default memo(ActivityPanelPragmatic);
